import { useState, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import EditorInput from "../EditorInput";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import {
	ComponentPropsContext,
	ComponentPropsContextApi
} from "layouts/Flow Designer/context";
import { EditorExpansion } from "../../../EditorExpansion";
import { useDebouncer } from "hooks/useDebouncer";

const PayloadEditorInput = ({
	action,
	fieldValue,
	label,
	allowEditWorkflow
}) => {
	const { setActionForm, setFormErrors } = useContext(
		ComponentPropsContextApi
	);
	const { actionForm, formErrors } = useContext(ComponentPropsContext);
	const [editorExpanded, setEditorExpanded] = useState(false);
	const [editorValue, setEditorValue] = useState(fieldValue);
	const { debouncer } = useDebouncer(handleEditorChange);

	const handleDebounceValue = (value) => {
		setEditorValue(value);
		debouncer(value);
	};

	function handleEditorChange(value) {
		if (formErrors.includes(action.field) && value !== "") {
			setFormErrors(formErrors.filter((error) => error !== action.field));
		}

		if (value === "") {
			setFormErrors((prev) => [...prev, action.field]);
		}

		const originalValue = value;
		const parsedQuery = originalValue.replace(/\\r\\n/g, "");

		setActionForm({
			...actionForm,
			[action.field]: { ...actionForm[action.field], value: parsedQuery }
		});
	}

	// Disable background scroll in fullscreen
	useEffect(() => {
		document.body.style.overflow = editorExpanded ? "hidden" : "";
	}, [editorExpanded]);

	const renderEditor = (
		<EditorInput
			fieldValue={editorValue}
			language={
				action.field === "response"
					? (actionForm.responseType.value ?? "json")
					: "json"
			}
			onChange={handleDebounceValue}
			options={{
				wordWrap: "off",
				scrollbar: {
					vertical: "auto",
					horizontal: "auto",
					verticalScrollbarSize: 8,
					domReadOnly: !allowEditWorkflow
				}
			}}
			height={editorExpanded ? "100%" : "150px"}
		/>
	);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				width: "100%"
			}}
		>
			<SoftTypography
				sx={{ paddingBottom: 1 }}
				variant="caption"
				fontWeight="bold"
				fontSize="14px"
				color="dark"
			>
				{label}
			</SoftTypography>

			{!editorExpanded && (
				<SoftBox sx={{ position: "relative" }}>
					{renderEditor}
					<EditorExpansion
						isExpanded={editorExpanded}
						handleExpansion={() => setEditorExpanded(true)}
					/>
				</SoftBox>
			)}

			{editorExpanded &&
				createPortal(
					<SoftBox
						sx={{
							position: "fixed",
							inset: 0,
							zIndex: 99999,
							width: "100vw",
							height: "100vh",
							backgroundColor: "rgba(20, 20, 20, 1)",
							display: "flex",
							flexDirection: "column"
						}}
					>
						<SoftBox sx={{ flex: 1, position: "relative" }}>
							{renderEditor}
							<SoftBox
								sx={{
									position: "absolute",
									bottom: 16,
									right: 16,
									zIndex: 100000
								}}
							>
								<EditorExpansion
									isExpanded={editorExpanded}
									handleExpansion={() => setEditorExpanded(false)}
								/>
							</SoftBox>
						</SoftBox>
					</SoftBox>,
					document.body
				)}
		</div>
	);
};

export default PayloadEditorInput;
